import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useRecoilState } from 'recoil';
import { Button } from '@storybook';
import { useNotification, useNetwork } from 'hooks';
import { timezoneState } from 'global-stores/timezone';
import { API_URL, WARNINGS } from 'constant';
import { handleTimezoneApiError, validateTimezoneState } from './error-handling';
import { TimezoneModal } from './timezone-modal';
import './timezone-settings.scss';

export const TimezoneSettings: React.FC = () => {
	const [timezone, setTimezone] = useRecoilState(timezoneState);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const { errorNotification, successNotification } = useNotification();
	const {
		post: changeSettings,
		data: settingsData,
		loading,
		error: timezoneSettingsError,
	} = useNetwork();

	const { SAVED, FAILED } = WARNINGS;

	const handleOpenModal = () => {
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	const handleTimezoneChange = useCallback(async (newTimezone: typeof timezone) => {
		// Validate timezone before making API call
		const validationError = validateTimezoneState(newTimezone);
		if (validationError) {
			errorNotification(validationError.message);
			return;
		}

		try {
			const timeZone = newTimezone.timezone;
			await changeSettings(`${API_URL.CLIENT_DETAILS}`, { timeZone });
			setTimezone(newTimezone);
		} catch (error) {
			const timezoneError = handleTimezoneApiError(error);
			errorNotification(timezoneError.message);
		}
	}, [changeSettings, setTimezone, errorNotification]);

	useEffect(() => {
		if (settingsData) {
			successNotification(SAVED);
			setIsModalOpen(false);
		}
		if (timezoneSettingsError) {
			const errorMsg = timezoneSettingsError?.message || FAILED;
			errorNotification(errorMsg);
		}
		// eslint-disable-next-line
	}, [settingsData, timezoneSettingsError, successNotification, errorNotification]);

	const renderTimezoneInfo = useMemo(() => (
		<div className="timezone-settings__selected">
			<div className="timezone-settings__selected-label">
				Selected Time Zone
			</div>
			<div className="timezone-settings__selected-value">
				{timezone.displayName}
			</div>
		</div>
	), [timezone.displayName]);

	return (
		<div className="timezone-settings">
			<div className="whitelabel-settings-wrapper hover w-50-8-gap-small-100">
				<div className="whitelabel-settings">
					<div className="whitelabel-settings__label-wrapper">
						<div className="whitelabel-settings__label-icon timezone-settings__icon">
							<i className="ri-time-zone-fill whitelabel-settings__icon" />
						</div>
						<div>
							<div className="whitelabel-settings__label-text">
								Time Zone
							</div>
							<div className="whitelabel-settings__desp">
								Your preferred time zone for reports, documents and notifications.
							</div>
						</div>
					</div>
					<div className="whitelabel-settings__action">
						<Button
							label="Change"
							type="button__outline--primary"
							prefixIcon="ri-edit-line"
							handleClick={handleOpenModal}
							disabled={loading}
						/>
					</div>
				</div>
				
				{renderTimezoneInfo}
			</div>

			<TimezoneModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				currentTimezone={timezone}
				onTimezoneChange={handleTimezoneChange}
				loading={loading}
			/>
		</div>
	);
};
